import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataService } from "@/services/data";
import { formatNumber } from "@/utils/format-fr";
import { useDebounce } from "@/utils/misc";
import { Loader } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterTreeSentence } from "./filter-tree-summary";
import { FilterTreeEditor } from "./tree-editor";
import { appendChild, countConditions, emptyTree, incompleteNodeIds, normalizeTree, pruneTree, treeSignature, updateNode } from "./tree-utils";
import type { FilterNode, FilterTree } from "./types";
import { FilterSchemaTile } from "./schema-tile";
import { useFilterSchema } from "./use-filter-schema";

type CountState = { status: "idle" | "loading" | "done" | "error"; count?: number; message?: string; nodeId?: string | null };

/** The server's answer to a tree it refused: `{filter_tree: [message], node_id}`. */
async function readTreeError(error: unknown): Promise<{ message: string; nodeId: string | null }> {
    const fallback = { message: "These filters cannot be applied.", nodeId: null };
    const body = (error as { json?: unknown; text?: string })?.json ?? safeParse((error as { text?: string })?.text);
    if (!body || typeof body !== "object") return fallback;
    const record = body as Record<string, unknown>;
    const messages = record.filter_tree;
    const message = Array.isArray(messages) ? String(messages[0]) : typeof messages === "string" ? messages : fallback.message;
    return { message, nodeId: typeof record.node_id === "string" ? record.node_id : null };
}

function safeParse(text?: string): unknown {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
}

/**
 * The draft of an advanced filter editor: the tree being edited, its live count on the list's
 * `count/` endpoint, and what blocks applying it. Shared by the dialog and the editors that show
 * the tree in place (the feeding's ListView filters).
 */
export function useFilterTreeDraft({ active, tree, countUrl }: { active: boolean; tree: FilterTree | null | undefined; countUrl?: string }) {
    const [draft, setDraft] = useState<FilterTree>(() => normalizeTree(tree ?? emptyTree()));
    const [focusId, setFocusId] = useState<string | null>(null);
    const [showIncomplete, setShowIncomplete] = useState(false);
    const [countState, setCountState] = useState<CountState>({ status: "idle" });

    useEffect(() => {
        if (active) {
            setDraft(normalizeTree(tree ?? emptyTree()));
            setShowIncomplete(false);
            setFocusId(null);
        }
    }, [active, tree]);

    const update = useCallback((id: string, change: (node: FilterNode) => FilterNode | null) => setDraft(previous => updateNode(previous, id, change)), []);
    const append = useCallback((groupId: string, node: FilterNode) => {
        setDraft(previous => appendChild(previous, groupId, node));
        setFocusId(node.id);
    }, []);

    const pruned = useMemo(() => pruneTree(draft), [draft]);
    const signature = useMemo(() => treeSignature(draft), [draft]);
    const debouncedSignature = useDebounce(signature, 500);
    const incomplete = useMemo(() => incompleteNodeIds(draft.root), [draft]);

    useEffect(() => {
        if (!active || !countUrl) return;
        let cancelled = false;
        setCountState(previous => ({ ...previous, status: "loading" }));
        const body = pruned.root.children.length ? { filter_tree: pruned } : {};
        DataService.client
            .url(countUrl)
            .post(body)
            .json<{ count: number }>()
            .then(data => !cancelled && setCountState({ status: "done", count: data.count }))
            .catch(async error => {
                if (cancelled) return;
                const { message, nodeId } = await readTreeError(error);
                setCountState({ status: "error", message, nodeId });
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, countUrl, debouncedSignature]);

    const errors = useMemo(() => {
        const messages: Record<string, string> = {};
        if (showIncomplete) incomplete.forEach(id => (messages[id] = "Complete or remove this condition."));
        if (countState.status === "error" && countState.nodeId) messages[countState.nodeId] = countState.message ?? "Invalid condition.";
        return messages;
    }, [showIncomplete, incomplete, countState]);

    /** The tree to apply, or null while a condition is incomplete or refused by the server. */
    const validate = (): FilterTree | null => {
        if (incomplete.length) {
            setShowIncomplete(true);
            return null;
        }
        if (countState.status === "error") return null;
        return pruned;
    };

    return {
        draft,
        setDraft,
        update,
        append,
        pruned,
        incomplete,
        errors,
        countState,
        focusId,
        validate,
        conditions: countConditions(draft.root)
    };
}

/** The live number of records a draft matches, its incomplete conditions, or the server's refusal. */
export function FilterTreeCount({ countState, incomplete }: { countState: CountState; incomplete: string[] }) {
    return (
        <div className="text-sm text-gray-600" aria-live="polite">
            {countState.status === "loading" && (
                <span className="flex items-center gap-2 text-gray-500">
                    <Loader className="h-4 w-4 animate-spin" /> Counting...
                </span>
            )}
            {countState.status === "done" && (
                <span>
                    <strong className="tabular-nums">{formatNumber(countState.count ?? 0)}</strong> matching record{countState.count === 1 ? "" : "s"}
                    {incomplete.length > 0 && (
                        <span className="text-gray-500">
                            {" "}
                            · {incomplete.length} incomplete condition{incomplete.length > 1 ? "s" : ""} ignored
                        </span>
                    )}
                </span>
            )}
            {countState.status === "error" && <span className="text-red-700">{countState.message}</span>}
        </div>
    );
}

/**
 * The advanced filters of a list, in a large dialog: groups of conditions (and / or), conditions
 * on fields of linked objects, and related records blocks (has at least one / has no / has only).
 * Edits are a draft: « Apply » hands the tree to the list, which sends it to the server as is.
 */
export function FilterTreeDialog({
    open,
    onOpenChange,
    rootModel,
    tree,
    onApply,
    countUrl
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** "app_label.model_name" of the listed object. */
    rootModel: string;
    tree: FilterTree | null | undefined;
    onApply: (tree: FilterTree) => void;
    /** `count/` endpoint of the list, for the live number of matching records. */
    countUrl?: string;
}) {
    const editor = useFilterTreeDraft({ active: open, tree, countUrl });
    const { schema } = useFilterSchema(open ? rootModel : null);

    const apply = () => {
        const next = editor.validate();
        if (!next) return;
        onApply(next);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-5xl">
                <DialogHeader className="border-b px-6 py-4">
                    <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
                        {schema && <FilterSchemaTile schema={schema} size="md" />}
                        Filter {schema?.name_plural ?? "records"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-gray-500">Combine conditions on this object, on the objects it links to, and on its related records.</DialogDescription>
                </DialogHeader>
                <div className="min-h-[240px] flex-1 overflow-y-auto px-6 py-4">
                    <FilterTreeSentence model={rootModel} tree={editor.draft} className="mb-3" />
                    <FilterTreeEditor rootModel={rootModel} root={editor.draft.root} update={editor.update} append={editor.append} errors={editor.errors} focusId={editor.focusId} />
                </div>
                <DialogFooter className="flex items-center border-t px-6 py-3 sm:justify-between">
                    <FilterTreeCount countState={editor.countState} incomplete={editor.incomplete} />
                    <div className="flex items-center gap-2">
                        <Button type="button" variant="ghost" disabled={editor.conditions === 0} onClick={() => editor.setDraft(emptyTree())}>
                            Clear all
                        </Button>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={apply} disabled={editor.countState.status === "error"}>
                            Apply
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
